'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Navigation,
  Pagination,
  Autoplay,
  A11y,
  Keyboard,
  EffectCoverflow,
  EffectFade,
} from 'swiper/modules';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import 'swiper/css';

import { Button, HeroIcon } from '@/components/atoms';
import { getCarouselBreakpoints } from '@/components/molecules';
import { ContentTypeVariations } from '@/components/organisms';
import { cn } from '@/lib/utils/tailwind';

import type { Swiper as SwiperCore } from 'swiper';

interface CarouselProps {
  children: ReactNode[];
  className?: string;
  autoplay?: boolean;
  loop?: boolean;
  navigation?: boolean;
  pagination?: boolean;
  effect?: 'coverflow' | 'fade' | 'cube' | 'default';
  slidesPerView?: number;
  spaceBetween?: number;
  width?: string;
  type?: ContentTypeVariations | string;
  justifyContent?: string;
}

export const Carousel = ({
  children,
  autoplay = true,
  loop = false,
  navigation = true,
  pagination = false,
  effect = 'default',
  slidesPerView = 3,
  spaceBetween = 1,
  width = '',
  type = ContentTypeVariations.VIDEO,
  justifyContent = 'flex-center',
}: CarouselProps) => {
  const t = useTranslations();
  const swiperRef = useRef<SwiperRef>(null);

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(children.length > slidesPerView);

  const updateNav = (swiper: SwiperCore) => {
    setCanPrev(!swiper.isBeginning);
    setCanNext(!swiper.isEnd);
  };

  useEffect(() => {
    if (swiperRef.current) updateNav(swiperRef.current.swiper);
  }, [children.length, slidesPerView]);

  const handlePrev = () => swiperRef.current?.swiper.slidePrev();
  const handleNext = () => swiperRef.current?.swiper.slideNext();

  const showNav = navigation && !swiperRef.current?.swiper?.isLocked;

  return (
    <div
      className="w-full relative overflow-visible"
      role="region"
      aria-roledescription="carousel"
    >
      <Swiper
        ref={swiperRef}
        modules={[
          Navigation,
          Pagination,
          Autoplay,
          A11y,
          Keyboard,
          EffectCoverflow,
          EffectFade,
        ]}
        loop={loop}
        autoplay={
          autoplay ? { delay: 3000, disableOnInteraction: false } : false
        }
        navigation={false}
        pagination={pagination ? { clickable: true } : false}
        keyboard={{ enabled: true }}
        effect={effect !== 'default' ? effect : undefined}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        coverflowEffect={
          effect === 'coverflow'
            ? {
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
              }
            : undefined
        }
        fadeEffect={effect === 'fade' ? { crossFade: true } : undefined}
        grabCursor
        centeredSlides={false}
        breakpoints={getCarouselBreakpoints(type)}
        onInit={updateNav}
        onSlideChange={updateNav}
        onResize={sw => updateNav(sw as SwiperCore)}
        onBreakpoint={_sw => updateNav(_sw)}
      >
        {children.map((child, i) => (
          <SwiperSlide key={i} className={width}>
            <div className={`flex ${justifyContent}`}>{child}</div>
          </SwiperSlide>
        ))}
      </Swiper>

      {showNav && (
        <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between pointer-events-none z-[1]">
          <Button
            variant="secondary"
            onClick={handlePrev}
            aria-label={t('PREVIOUS_SLIDE')}
            className={cn(
              'pointer-events-auto rounded-(--button-primary-radius-default) shadow-md',
              !canPrev && 'opacity-0',
            )}
          >
            <HeroIcon icon={ChevronLeftIcon} size="small" />
          </Button>

          <Button
            variant="secondary"
            onClick={handleNext}
            aria-label={t('NEXT_SLIDE')}
            className={cn(
              'pointer-events-auto rounded-(--button-primary-radius-default) shadow-md',
              !canNext && 'opacity-0',
            )}
          >
            <HeroIcon icon={ChevronRightIcon} size="small" />
          </Button>
        </div>
      )}
    </div>
  );
};
